import React, { useRef } from "react";

import Container from "../../../../../../../../components/Container";
import TextInput from "../../../../../../../../components/Inputs/InputText";
import SelectInput from "../../../../../../../../components/Inputs/SelectInput";
import TruncatedSelectInput from "../../../../../../../../components/Inputs/SelectInput/TruncatedSelectInput";
import Text from "../../../../../../../../components/Text";
import Config from "./Config";

import { ChartType, nineBoxRequiredFields } from "../../../../store/enums";

import { hasEnoughRoomBelowForElements } from "../ChartHooks/utils";

import scatterConfig from "../ChartHooks/ScatterChart/Config";
import useChartOptions from "../ChartHooks/UseChartOptions/useChartOptions";
import OptionSettings, { dataSeriesTooltipText } from "../shared/OptionSettings";

const { dataSeriesRangeOptions } = OptionSettings[ChartType.ScatterChart];

const NineBoxChartOptions = () => {
	const chartOptions = useChartOptions(ChartType.NineBox);
	const optionsRefX = useRef<HTMLDivElement>(null);

	const {
		setYAxisColumnData,
		setXAxisColumnData,
		setDataSeries,
		visualizeOption,
		anyAxisOptions,
		previousVisualizeOption,
	} = chartOptions;

	// filter for ninebox data series options
	const dataSeriesOptions = chartOptions.dataSeriesOptions.filter((option) =>
		nineBoxRequiredFields.has(option.value.toLowerCase())
	);

	const selectedRange =
		(visualizeOption?.dataSeries?.rangeOption &&
			dataSeriesRangeOptions.map((val) => val.value).includes(visualizeOption.dataSeries.rangeOption) &&
			visualizeOption.dataSeries.rangeOption) ||
		scatterConfig.DEFAULT_RANGE_OPTION;

	const DataSeriesOptions = (
		<>
			<SelectInput
				label="Data Series"
				labelProps={{ color: "neutral-9", weight: "semi-bold", size: "paragraph-3" }}
				menuPortalTarget={document.body}
				menuPosition="fixed"
				results={dataSeriesOptions}
				value={visualizeOption?.dataSeries?.column}
				onChange={(option) => setDataSeries("column", option.value)}
				info={{
					title: "Data Series",
					message: dataSeriesTooltipText,
					titleBottomLine: true,
				}}
			/>
			<Container marginTop={1} marginBottom="3px">
				<Container centerVertical marginBottom="5px" width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Range
					</Text>
				</Container>
				<Container block>
					<SelectInput
						value={selectedRange}
						results={dataSeriesRangeOptions}
						onChange={(option) => setDataSeries("rangeOption", option.value)}
					/>
				</Container>
			</Container>
		</>
	);

	const YAxisOptions = (
		<>
			<TruncatedSelectInput
				label={Config.Y_AXIS_LABEL}
				labelProps={{ color: "neutral-9", weight: "semi-bold", size: "paragraph-3" }}
				results={anyAxisOptions}
				value={
					visualizeOption?.yAxis?.column ||
					previousVisualizeOption?.yAxis?.column ||
					previousVisualizeOption?.xAxis?.column
				}
				menuPortalTarget={document.body}
				menuPosition="fixed"
				onChange={(option) => {
					setYAxisColumnData("column", option.value);
				}}
			/>
			<Container marginTop={1} marginBottom={1}>
				<Container centerVertical marginBottom="5px" width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Minimum
					</Text>
				</Container>
				<Container block>
					<TextInput
						type="number"
						value={visualizeOption?.yAxis?.min ?? 0}
						onChange={(value) => setYAxisColumnData("min", value)}
						testId="nine-box-y-axis-min"
						ariaLabel="Y-Axis Set Minimum"
					/>
				</Container>
			</Container>
			<Container marginBottom={1}>
				<Container centerVertical marginBottom="5px" width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Maximum
					</Text>
				</Container>
				<Container block>
					<TextInput
						type="number"
						value={visualizeOption?.yAxis?.max ?? 100}
						onChange={(value) => setYAxisColumnData("max", value)}
						testId="nine-box-y-axis-max"
						ariaLabel="Y-Axis Set Maximum"
					/>
				</Container>
			</Container>
		</>
	);

	const XAxisOptions = (
		<>
			<span ref={optionsRefX}>
				<TruncatedSelectInput
					label={Config.X_AXIS_LABEL}
					labelProps={{ color: "neutral-9", weight: "semi-bold", size: "paragraph-3" }}
					value={
						visualizeOption?.xAxis?.column ||
						previousVisualizeOption?.xAxis?.column ||
						previousVisualizeOption?.yAxis?.column
					}
					menuPortalTarget={optionsRefX.current}
					menuPosition="fixed"
					menuPlacement={hasEnoughRoomBelowForElements(optionsRefX.current, anyAxisOptions) ? "auto" : "top"}
					results={anyAxisOptions}
					onChange={(option) => {
						setXAxisColumnData("column", option.value);
					}}
				/>
			</span>
			<Container marginTop={1} marginBottom={1}>
				<Container centerVertical width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Minimum
					</Text>
				</Container>
				<Container block>
					<TextInput
						type="number"
						value={visualizeOption?.xAxis?.min ?? 0}
						onChange={(value) => setXAxisColumnData("min", value)}
						testId="nine-box-x-axis-min"
						ariaLabel="X-Axis Set Minimum"
					/>
				</Container>
			</Container>
			<Container marginBottom={1}>
				<Container centerVertical width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Maximum
					</Text>
				</Container>
				<Container block>
					<TextInput
						type="number"
						value={visualizeOption?.xAxis?.max ?? 100}
						onChange={(value) => setXAxisColumnData("max", value)}
						testId="nine-box-x-axis-max"
						ariaLabel="X-Axis Set Maximum"
					/>
				</Container>
			</Container>
		</>
	);

	return (
		<>
			{DataSeriesOptions}
			{XAxisOptions}
			{YAxisOptions}
		</>
	);
};

export default NineBoxChartOptions;
